import { createFileRoute } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getLowStock } from "@/lib/inventory-views.functions";
import { useScope } from "@/hooks/use-scope";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/ui/status-pill";
import { AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/inventory/low-stock")({ component: LowStockPage });

function LowStockPage() {
  const { tenantId, facilityId, customerId, customers } = useScope();
  const getLowStockFn = useServerFn(getLowStock);

  const { data, isLoading } = useQuery({
    queryKey: ["low-stock", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getLowStockFn({ data: {
      tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null,
    } }),
    placeholderData: keepPreviousData,
  });

  const customerName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-warning" />
        <div>
          <h2 className="text-lg font-semibold">Low Stock</h2>
          <p className="text-sm text-muted-foreground">SKUs at or below their reorder point in this facility.</p>
        </div>
      </div>
      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">On hand</TableHead>
              <TableHead className="text-right">Reorder pt</TableHead>
              <TableHead className="text-right">Shortfall</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : data && data.length > 0 ? data.map((s) => {
              const shortfall = s.reorder_point - s.on_hand;
              return (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-sm">{s.sku_code}</TableCell>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-muted-foreground">{customerName(s.customer_id)}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.on_hand}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{s.reorder_point}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <StatusPill tone={s.on_hand <= 0 ? "danger" : "warning"}>
                      {shortfall > 0 ? `+${shortfall}` : "0"}
                    </StatusPill>
                  </TableCell>
                </TableRow>
              );
            }) : (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">All SKUs are above their reorder point.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}